// FR-7 / FR-9 settings: all preferences persisted to localStorage, instant-effect.

import type { Page } from '../page';
import { el } from '../components';
import { state, toast } from '../store';
import type { PitchTolerancePreset } from '../../types';

const PRESET_CENTS: Record<PitchTolerancePreset, number> = { pro: 5, advanced: 10, beginner: 15 };

export function settingsPage(): Page {
  return {
    mount(container) {
      const p = state.prefs;
      const online = state.online;

      const a4Slider = el('input', { type: 'range', min: '410', max: '470', step: '0.1', value: String(p.A4) });
      const a4Val = el('span', { class: 'muted', text: `${p.A4.toFixed(1)} Hz` });
      a4Slider.oninput = () => { const v = +(a4Slider as HTMLInputElement).value; state.setPrefs({ A4: v }); a4Val.textContent = `${v.toFixed(1)} Hz`; };

      const offsetSlider = el('input', { type: 'range', min: '-200', max: '200', step: '10', value: String(p.midiOffset) });
      const offsetVal = el('span', { class: 'muted', text: `${p.midiOffset} ms` });
      offsetSlider.oninput = () => { const v = Number(offsetSlider.value); state.setPrefs({ midiOffset: v }); offsetVal.textContent = `${v} ms`; };

      const autoToggle = el('input', { type: 'checkbox' });
      (autoToggle as HTMLInputElement).checked = p.autoStringDetection;
      autoToggle.onchange = () => state.setPrefs({ autoStringDetection: (autoToggle as HTMLInputElement).checked });

      const thrSlider = el('input', { type: 'range', min: '0', max: '100', step: '1', value: String(p.timbreThreshold) });
      const thrVal = el('span', { class: 'muted', text: `${p.timbreThreshold} 分` });
      thrSlider.oninput = () => { const v = Number(thrSlider.value); state.setPrefs({ timbreThreshold: v }); thrVal.textContent = `${v} 分`; };

      const tolSlider = el('input', { type: 'range', min: '1', max: '50', step: '1', value: String(p.pitchTolerance) });
      const tolVal = el('span', { class: 'muted', text: `±${p.pitchTolerance} cents` });
      tolSlider.oninput = () => { const v = Number(tolSlider.value); state.setPrefs({ pitchTolerance: v, pitchTolerancePreset: 'beginner' }); tolVal.textContent = `±${v} cents`; presetChips.forEach((c) => c.classList.remove('active')); };

      const presetChips = (['pro', 'advanced', 'beginner'] as PitchTolerancePreset[]).map((k) => {
        const b = el('button', { class: 'chip' + (p.pitchTolerancePreset === k ? ' active' : ''), text: `${k === 'pro' ? '专业' : k === 'advanced' ? '进阶' : '初学'} ±${PRESET_CENTS[k]}` });
        b.onclick = () => { state.setPrefs({ pitchTolerance: PRESET_CENTS[k], pitchTolerancePreset: k }); tolSlider.value = String(PRESET_CENTS[k]); tolVal.textContent = `±${PRESET_CENTS[k]} cents`; presetChips.forEach((c) => c.classList.remove('active')); b.classList.add('active'); };
        return b;
      });

      const langChips = (['zh', 'en'] as const).map((l) => {
        const b = el('button', { class: 'chip' + (p.language === l ? ' active' : ''), text: l === 'zh' ? '中文' : 'English' });
        b.onclick = () => { state.setPrefs({ language: l }); langChips.forEach((c) => c.classList.remove('active')); b.classList.add('active'); toast(l === 'zh' ? '已切换为中文' : 'Switched to English'); };
        return b;
      });

      const resetBtn = el('button', { class: 'chip', text: '恢复默认设置' });
      resetBtn.onclick = () => { state.resetPrefs(); toast('已恢复默认'); setTimeout(() => location.reload(), 600); };

      container.append(
        el('h1', { text: '设置' }),
        el('p', { class: 'sub', text: '所有参数即时生效并保存到本地' }),
        el('div', { class: 'card' }, [
          el('h2', { text: 'A4 参考频率' }),
          el('div', { class: 'row' }, [a4Slider, a4Val]),
          el('p', { class: 'tiny muted', text: '范围 410–470 Hz，步进 0.1 Hz，影响调音/跟弹/音色全部音高计算。' }),
        ]),
        el('div', { class: 'card' }, [
          el('h2', { text: 'MIDI Offset（伴奏提前量）' }),
          el('div', { class: 'row' }, [offsetSlider, offsetVal]),
          el('p', { class: 'tiny muted', text: '范围 -200~+200 ms，步进 10 ms，默认 -50 ms。用于补偿蓝牙耳机延迟。' }),
        ]),
        el('div', { class: 'card' }, [
          el('h2', { text: '弦识别模式' }),
          el('label', { class: 'row', style: 'gap:8px' }, [autoToggle, el('span', { text: '自动识别琴弦（关闭后手动锁定）' })]),
        ]),
        el('div', { class: 'card' }, [
          el('h2', { text: '音色及格线' }),
          el('div', { class: 'row' }, [thrSlider, thrVal]),
        ]),
        el('div', { class: 'card' }, [
          el('h2', { text: '跟弹音准容差' }),
          el('div', { class: 'row wrap' }, presetChips),
          el('div', { class: 'row', style: 'margin-top:8px' }, [tolSlider, tolVal]),
          el('p', { class: 'tiny muted', text: '高级：1–50 cents 无级滑块。调音"已准"阈值 ±5 cents 与跟弹容差不联动。' }),
        ]),
        el('div', { class: 'card' }, [
          el('h2', { text: '语言' }),
          el('div', { class: 'row wrap' }, langChips),
        ]),
        el('div', { class: 'card' }, [
          el('h2', { text: '其他' }),
          el('div', { class: 'row between' }, [
            el('span', { class: 'tiny muted', text: online ? '🟢 在线' : '🔴 离线模式' }),
            resetBtn,
          ]),
          el('p', { class: 'tiny muted', text: '练习记录与乐谱缓存于 IndexedDB，可在「报告」页导出全部数据。' }),
        ]),
        el('p', { class: 'tiny muted', text: '🔒 所有音频与乐谱均在本地处理，不上传云端。user_role 默认 student，V2 教师端数据模型已预留。' }),
      );
    },
  };
}
